import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Divider,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { formatDate } from "utils/format";
import referralsService from "services/referralsService";

const PAGE_SIZE = 20;

export default function ReferalsUnprocessed() {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const badgeBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
  const cardBg = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.100");
  const rowHoverBg = useColorModeValue("secondaryGray.50", "whiteAlpha.50");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);

  const fetchUnprocessedReferrals = async (nextSkip = 0) => {
    setLoading(true);
    setError(null);
    try {
      const result = await referralsService.getUnprocessedReferrals({
        skip: nextSkip,
        limit: PAGE_SIZE,
      });

      if (result.success && result.data) {
        setReferrals(result.data.referrals || []);
        setTotal(result.data.total || 0);
        setSkip(result.data.skip || 0);
      } else {
        setError(result.error || "Failed to load unprocessed referrals");
      }
    } catch (err) {
      console.error("Error loading unprocessed referrals:", err);
      setError("Failed to load unprocessed referrals. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnprocessedReferrals(0);
  }, []);

  const canGoPrev = skip > 0;
  const canGoNext = skip + PAGE_SIZE < total;

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card p="30px" bg={cardBg}>
        <Flex align="center" justify="space-between" mb="20px" flexWrap="wrap" gap="10px">
          <Text color={textColor} fontSize="2xl" fontWeight="700">
            Unprocessed Referals
          </Text>
          <Text color={textColorSecondary} fontSize="sm">
            Total: {total}
          </Text>
        </Flex>
        <Divider borderColor={borderColor} mb="20px" />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minH="220px">
            <Spinner size="lg" color="brand.500" />
          </Box>
        ) : error ? (
          <Alert status="error" borderRadius="12px">
            <AlertIcon />
            <Text color={textColor}>{error}</Text>
          </Alert>
        ) : (
          <>
            <Box overflowX="auto">
              <Table variant="simple" color="secondaryGray.700">
                <Thead>
                  <Tr>
                    <Th color={textColorSecondary}>SN</Th>
                    <Th color={textColorSecondary}>Name</Th>
                    <Th color={textColorSecondary}>Email</Th>
                    <Th color={textColorSecondary}>Phone</Th>
                    <Th color={textColorSecondary}>Referral Code</Th>
                    <Th color={textColorSecondary}>Status</Th>
                    <Th color={textColorSecondary}>Created At</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {referrals.length > 0 ? (
                    referrals.map((referral) => (
                      <Tr key={referral.id} _hover={{ bg: rowHoverBg }}>
                        <Td>
                          <Text color={textColor} fontSize="sm">
                            {referral.sn ?? "-"}
                          </Text>
                        </Td>
                        <Td>
                          <Text color={textColor} fontSize="sm" fontWeight="600">
                            {referral.name || "-"}
                          </Text>
                        </Td>
                        <Td>
                          <Text color={textColor} fontSize="sm">
                            {referral.email || "-"}
                          </Text>
                        </Td>
                        <Td>
                          <Text color={textColor} fontSize="sm">
                            {referral.phone_number || "-"}
                          </Text>
                        </Td>
                        <Td>
                          <Text color={textColor} fontSize="sm" fontFamily="mono">
                            {referral.referral_code || "-"}
                          </Text>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme="orange"
                            bg={badgeBg}
                            color={textColor}
                            fontSize="xs"
                            fontWeight="500"
                            px="8px"
                            py="4px"
                            borderRadius="8px"
                          >
                            {(referral.processing_status || "unprocessed").toUpperCase()}
                          </Badge>
                        </Td>
                        <Td>
                          <Text color={textColor} fontSize="sm">
                            {referral.created_at ? formatDate(referral.created_at) : "-"}
                          </Text>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py="20px">
                        <Text color={textColorSecondary} fontSize="sm">
                          No unprocessed referrals found
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>

            <Flex justify="space-between" align="center" mt="20px">
              <Text color={textColorSecondary} fontSize="xs">
                Showing {Math.min(skip + 1, total)}-{Math.min(skip + PAGE_SIZE, total)} of {total}
              </Text>
              <Flex gap="10px">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchUnprocessedReferrals(Math.max(0, skip - PAGE_SIZE))}
                  isDisabled={!canGoPrev}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => fetchUnprocessedReferrals(skip + PAGE_SIZE)}
                  isDisabled={!canGoNext}
                >
                  Next
                </Button>
              </Flex>
            </Flex>
          </>
        )}
      </Card>
    </Box>
  );
}
